import _ from 'lodash';
import { AppStateValue } from '../../AppState';
import { ScopedInstanceMember, ScopedType } from '../../shared/interfaces/scopeInstanceMember.interface';

// How a member is drawn. Independent of which bucket it lands in - the Entra
// picker hands back both users and groups, so that bucket mixes the two. #34542
export enum ScopeMemberKind {
  Team = 'team',
  Group = 'group',
  Person = 'person'
}

// The groups Available To is split into. One per control in the sharing picker,
// in the same order, so the read only view mirrors how sharing was set up.
export enum ScopeBucket {
  Entra = 'entra',
  Team = 'team',
  Group = 'group',
  Assignee = 'assignee'
}

export interface ResolvedScopeMember {
  bucket: ScopeBucket;
  kind: ScopeMemberKind;
  // Stable per row, used as the React key.
  key: string;
  // Entra object id. Teams use it for the group photo, people for the person
  // photo. Undefined for a support group, which has no Entra identity.
  aadObjectId?: string;
  // Teams and groups carry their name from app state. People are left blank and
  // resolved by mgt-person, the same way every other avatar in the app is.
  name?: string;
}

// The order the buckets are drawn in, matching the sharing picker's controls. It
// only affected the hover panel until the cell started collapsing to fit a width -
// a total is order independent, but which buckets survive the collapse is not.
export const BUCKET_ORDER: ScopeBucket[] = [ScopeBucket.Entra, ScopeBucket.Team, ScopeBucket.Group, ScopeBucket.Assignee];

export const bucketOf = (scopedType: string): ScopeBucket => {
  switch (scopedType) {
    case ScopedType.EntraUser:
    case ScopedType.EntraGroup:
      return ScopeBucket.Entra;
    case ScopedType.Team:
      return ScopeBucket.Team;
    case ScopedType.Group:
      return ScopeBucket.Group;
    default:
      return ScopeBucket.Assignee;
  }
};

const kindOf = (scopedType: string): ScopeMemberKind => {
  switch (scopedType) {
    case ScopedType.Team:
      return ScopeMemberKind.Team;
    case ScopedType.Group:
    case ScopedType.EntraGroup:
      return ScopeMemberKind.Group;
    default:
      return ScopeMemberKind.Person;
  }
};

const nameOf = (member: ScopedInstanceMember, state: AppStateValue): string | undefined => {
  switch (member.ScopedType) {
    case ScopedType.Team:
      { const team = state.teams?.find(x => x.TeamsAadObjectId == member.AadObjectId);
        return team?.FriendlyName || team?.Name; }
    case ScopedType.Group:
      return state.supportgroups?.find(x => x.Id?.toString() == member.Member?.toString())?.Name;
    // An Entra group's name is fetched by id where it is displayed - see entraGroupNames.
    default:
      return undefined;
  }
};

export const resolveScopedMembers = (
  scopedMembers: ScopedInstanceMember[],
  state: AppStateValue
): ResolvedScopeMember[] =>
  (scopedMembers ?? []).map((member, i) => ({
    bucket: bucketOf(member.ScopedType),
    kind: kindOf(member.ScopedType),
    key: `${member.ScopedType}-${member.AadObjectId ?? member.Member ?? i}`,
    aadObjectId: member.AadObjectId,
    name: nameOf(member, state)
  }));

export const membersOfBucket = (members: ResolvedScopeMember[], bucket: ScopeBucket) =>
  members.filter(m => m.bucket === bucket);

// A share's identity is ScopedType + Member. ScopePermissionPicker's
// `changed` callback always rebuilds ScopedMembers from scratch, so it never
// carries over the server-assigned Id - that alone isn't a real change, only
// the identity set is. Order isn't meaningful either (it's a set of shares),
// so removing and re-adding the same member (in any order, alongside other
// edits) shouldn't be reported as a change.
export const haveScopedMembersChanged = (
  updated: ScopedInstanceMember[],
  initial: ScopedInstanceMember[]
): boolean => {
  const identity = (member: ScopedInstanceMember) => `${member?.ScopedType ?? ''}::${member?.Member ?? ''}`;
  const sortedIdentities = (members: ScopedInstanceMember[]) => (members ?? []).map(identity).sort((a, b) => a.localeCompare(b));

  return !_.isEqual(sortedIdentities(updated), sortedIdentities(initial));
};
